// src/utils/currentNavigator.js
//
// Resolving "which navigator row am I?" — needed by ProfilePage and by the
// forced password-change gate, both of which have to PUT/PATCH against the
// caller's own id.
//
// It is not simply user.id: the JWT subject is the EMAIL for navigator
// accounts, so AuthContext's decodeJwtId often stores an email string there.
//
// Both screens used to fall back to navigatorAPI.list() — fetching every staff
// member in the system to find one row. That was the only reason the list
// endpoint had to stay readable by non-admins, and it meant any staff member
// could enumerate every colleague's email and phone. GET /api/navigators/me
// replaced it, and the list endpoint is now @PreAuthorize("hasRole('ADMIN')").
//
// The fallback is gone with it. It was kept for one release so the frontend
// could deploy ahead of the API; now that both are live it can only 403 for a
// non-admin, turning one clear failure into a second doomed request and a
// vaguer message.

import { navigatorAPI } from '../services/api';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isValidUUID(val) {
  return !!val && UUID_RE.test(val);
}

/**
 * The caller's own navigator row.
 * @throws if the account cannot be resolved.
 */
export async function fetchCurrentNavigator() {
  const res = await navigatorAPI.me();
  if (!res.data?.id) {
    throw new Error('Could not resolve your account. Please sign out and back in.');
  }
  return res.data;
}

/**
 * The caller's own navigator id, using user.id directly when the JWT happened
 * to carry a UUID rather than an email.
 */
export async function resolveNavigatorId(user) {
  if (isValidUUID(user?.id)) return user.id;
  return (await fetchCurrentNavigator()).id;
}
